import * as React from 'react'
import type { TenantLoginResponseDto, TenantMembershipDto, TenantSessionDto } from '@lib/dto/tenant-auth.dto'
import type { AcceptInvitationInput, TenantLoginInput } from '@lib/input/tenant-auth.input'
import { uuidParamSchema, type Uuid } from '@lib/validators/params'
import { tenantTokenStorage } from '@/helpers/tokenStorage'
import logger, { toErrorMessage } from '@/helpers/logger'
import { isSessionRejected } from '@/auth/sessionRejected'
import { tenantAuthService } from '@/services/TenantAuthService'

const ACTIVE_TENANT_KEY = 'pixelsign.activeTenant'

interface TenantAuthContextValue {
  session: TenantSessionDto | null
  /** The membership the dashboard is currently scoped to, if any. */
  activeMembership: TenantMembershipDto | null
  isLoading: boolean
  isAuthenticated: boolean
  login: (input: TenantLoginInput) => Promise<void>
  acceptInvitation: (token: string, input: AcceptInvitationInput) => Promise<void>
  setActiveTenant: (tenantId: Uuid) => void
  /** Re-read the session (for example after a role change). */
  refresh: () => Promise<void>
  logout: () => void
}

const TenantAuthContext = React.createContext<TenantAuthContextValue | null>(null)

function readStoredTenant(): Uuid | null {
  try {
    const parsed = uuidParamSchema.safeParse(localStorage.getItem(ACTIVE_TENANT_KEY))
    return parsed.success ? parsed.data : null
  } catch {
    return null
  }
}

function storeTenant(tenantId: Uuid): void {
  try {
    localStorage.setItem(ACTIVE_TENANT_KEY, tenantId)
  } catch {
    // Storage can be unavailable (private mode); the choice just won't persist.
  }
}

/** The remembered tenant when the user still belongs to it, else their first. */
function pickMembership(
  session: TenantSessionDto | null,
  preferred: Uuid | null,
): TenantMembershipDto | null {
  if (!session) {
    return null
  }
  return (
    session.memberships.find((row) => row.tenantId === preferred) ??
    session.memberships[0] ??
    null
  )
}

/**
 * Tenant dashboard session: the signed-in tenant user, the tenants they belong
 * to, and which one the dashboard is showing. Independent of the staff
 * `AuthProvider`, with its own token.
 */
export function TenantAuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = React.useState<TenantSessionDto | null>(null)
  const [activeTenantId, setActiveTenantId] = React.useState<Uuid | null>(readStoredTenant)
  const [isLoading, setIsLoading] = React.useState<boolean>(true)

  React.useEffect(() => {
    let active = true
    async function restore(): Promise<void> {
      if (!tenantTokenStorage.get()) {
        setIsLoading(false)
        return
      }
      try {
        const current = await tenantAuthService.me()
        if (active) setSession(current)
      } catch (error) {
        logger.warn('Tenant session restore failed', { error: toErrorMessage(error) })
        // Only a rejected token ends the session; an unreachable API leaves it
        // for the next load to retry.
        if (isSessionRejected(error)) tenantTokenStorage.clear()
        if (active) setSession(null)
      } finally {
        if (active) setIsLoading(false)
      }
    }
    void restore()
    return () => {
      active = false
    }
  }, [])

  const begin = React.useCallback((result: TenantLoginResponseDto): void => {
    tenantTokenStorage.set(result.accessToken)
    setSession({ user: result.user, memberships: result.memberships })
  }, [])

  const login = React.useCallback(
    async (input: TenantLoginInput): Promise<void> => {
      begin(await tenantAuthService.login(input))
    },
    [begin],
  )

  const acceptInvitation = React.useCallback(
    async (token: string, input: AcceptInvitationInput): Promise<void> => {
      begin(await tenantAuthService.acceptInvitation(token, input))
    },
    [begin],
  )

  const setActiveTenant = React.useCallback((tenantId: Uuid): void => {
    const parsed = uuidParamSchema.safeParse(tenantId)
    if (!parsed.success) return
    storeTenant(parsed.data)
    setActiveTenantId(parsed.data)
  }, [])

  const refresh = React.useCallback(async (): Promise<void> => {
    setSession(await tenantAuthService.me())
  }, [])

  const logout = React.useCallback((): void => {
    void tenantAuthService.logout().catch((error) => {
      logger.warn('Server logout failed', { error: toErrorMessage(error) })
    })
    tenantTokenStorage.clear()
    setSession(null)
  }, [])

  const value: TenantAuthContextValue = {
    session,
    activeMembership: pickMembership(session, activeTenantId),
    isLoading,
    isAuthenticated: session !== null,
    login,
    acceptInvitation,
    setActiveTenant,
    refresh,
    logout,
  }

  return <TenantAuthContext value={value}>{children}</TenantAuthContext>
}

export function useTenantAuth(): TenantAuthContextValue {
  const context = React.use(TenantAuthContext)
  if (context === null) {
    throw new Error('useTenantAuth must be used within a TenantAuthProvider')
  }
  return context
}
